// UserForm.jsx
import React, {useState, useEffect} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {FaStar, FaMoon, FaSun} from 'react-icons/fa';

// Import components
import PlanetAnimation from '../components/PlanetAnimation';
import {
    FormField,
    BirthplaceField,
    AdvancedOptionsField,
    ConfirmationView,
    LoadingView
} from '../components/FormField';

// New custom date and time picker components
const DatePickerDropdown = ({value, onChange}) => {
    const [day, setDay] = useState('');
    const [month, setMonth] = useState('');
    const [year, setYear] = useState('');

    useEffect(() => {
        if (value) {
            const [y, m, d] = value.split('-');
            setYear(y);
            setMonth(m);
            setDay(d);
        }
    }, [value]);

    const updateDate = (newDay, newMonth, newYear) => {
        if (newDay !== undefined) setDay(newDay);
        if (newMonth !== undefined) setMonth(newMonth);
        if (newYear !== undefined) setYear(newYear);

        const updatedDay = newDay !== undefined ? newDay : day;
        const updatedMonth = newMonth !== undefined ? newMonth : month;
        const updatedYear = newYear !== undefined ? newYear : year;

        if (updatedDay && updatedMonth && updatedYear) {
            const formattedDate = `${updatedYear}-${updatedMonth.padStart(2, '0')}-${updatedDay.padStart(2, '0')}`;
            onChange({target: {name: 'dob', value: formattedDate}});
        }
    };

    // Always show all 31 days, validation will happen on form submission
    const days = Array.from({length: 31}, (_, i) => (i + 1).toString());

    // Months in text format
    const monthsText = [
        {value: "01", text: "January"},
        {value: "02", text: "February"},
        {value: "03", text: "March"},
        {value: "04", text: "April"},
        {value: "05", text: "May"},
        {value: "06", text: "June"},
        {value: "07", text: "July"},
        {value: "08", text: "August"},
        {value: "09", text: "September"},
        {value: "10", text: "October"},
        {value: "11", text: "November"},
        {value: "12", text: "December"}
    ];

    // Min and current year
    const minYear = 1900;
    const currentYear = new Date().getFullYear();

    return (
        <div className="flex space-x-2">
            {/* Month */}
            <div className="w-1/3 relative">
                <select
                    value={month}
                    onChange={(e) => updateDate(undefined, e.target.value, undefined)}
                    className="block w-full rounded-lg bg-black/30 text-white border border-white/10 px-3 py-3
                   focus:outline-none focus:ring-2 focus:ring-yellow-400/70 appearance-none"
                    required
                >
                    <option value="">Month</option>
                    {monthsText.map((m) => (
                        <option key={m.value} value={m.value}>
                            {m.text}
                        </option>
                    ))}
                </select>
                {/* custom chevron */}
                <svg
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-70"
                    viewBox="0 0 20 20" fill="currentColor"
                >
                    <path d="M10 12l-4-4h8l-4 4z" />
                </svg>
            </div>

            {/* Day */}
            <div className="w-1/3 relative">
                <select
                    value={day}
                    onChange={(e) => updateDate(e.target.value, undefined, undefined)}
                    className="block w-full rounded-lg bg-black/30 text-white border border-white/10 px-3 py-3
                   focus:outline-none focus:ring-2 focus:ring-yellow-400/70 appearance-none"
                    required
                >
                    <option value="">Day</option>
                    {days.map((d) => (
                        <option key={d} value={d}>{d}</option>
                    ))}
                </select>
                {/* custom chevron */}
                <svg
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-70"
                    viewBox="0 0 20 20" fill="currentColor"
                >
                    <path d="M10 12l-4-4h8l-4 4z" />
                </svg>
            </div>

            {/* Year */}
            <div className="w-1/3">
                <input
                    type="number"
                    min={minYear}
                    max={currentYear}
                    value={year}
                    onChange={(e) => updateDate(undefined, undefined, e.target.value)}
                    className="block w-full rounded-lg bg-black/30 text-white border border-white/10 px-3 py-3
                   focus:outline-none focus:ring-2 focus:ring-yellow-400/70"
                    placeholder="Year"
                    required
                />
            </div>
        </div>
    );

};

const TimePickerDropdown = ({value, onChange}) => {
    const [hour, setHour] = useState('');
    const [minute, setMinute] = useState('');

    useEffect(() => {
        if (value) {
            const [h, m] = value.split(':');
            setHour(h);
            setMinute(m);
        }
    }, [value]);

    const updateTime = (newHour, newMinute) => {
        if (newHour !== undefined) setHour(newHour);
        if (newMinute !== undefined) setMinute(newMinute);

        const updatedHour = newHour !== undefined ? newHour : hour;
        const updatedMinute = newMinute !== undefined ? newMinute : minute;

        if (updatedHour && updatedMinute) {
            const formattedTime = `${updatedHour}:${updatedMinute}`;
            onChange({target: {name: 'tob', value: formattedTime}});
        }
    };

    // Generate 24-hour options with AM/PM labels
    const hours24WithFormat = [
        {value: "00", text: "00 AM (00)"},
        {value: "01", text: "01 AM (01)"},
        {value: "02", text: "02 AM (02)"},
        {value: "03", text: "03 AM (03)"},
        {value: "04", text: "04 AM (04)"},
        {value: "05", text: "05 AM (05)"},
        {value: "06", text: "06 AM (06)"},
        {value: "07", text: "07 AM (07)"},
        {value: "08", text: "08 AM (08)"},
        {value: "09", text: "09 AM (09)"},
        {value: "10", text: "10 AM (10)"},
        {value: "11", text: "11 AM (11)"},
        {value: "12", text: "12 PM (12)"},
        {value: "13", text: "01 PM (13)"},
        {value: "14", text: "02 PM (14)"},
        {value: "15", text: "03 PM (15)"},
        {value: "16", text: "04 PM (16)"},
        {value: "17", text: "05 PM (17)"},
        {value: "18", text: "06 PM (18)"},
        {value: "19", text: "07 PM (19)"},
        {value: "20", text: "08 PM (20)"},
        {value: "21", text: "09 PM (21)"},
        {value: "22", text: "10 PM (22)"},
        {value: "23", text: "11 PM (23)"}
    ];

    // Generate options for minutes
    const minutes = Array.from({length: 60}, (_, i) => i.toString().padStart(2, '0'));

    return (
        <div className="flex space-x-2">
            {/* Hour */}
            <div className="w-1/2 relative">
                <select
                    value={hour}
                    onChange={(e) => updateTime(e.target.value, undefined)}
                    className="block w-full rounded-lg bg-black/30 text-white border border-white/10 px-3 py-3
                   focus:outline-none focus:ring-2 focus:ring-yellow-400/70 appearance-none"
                    required
                >
                    <option value="">Hour</option>
                    {hours24WithFormat.map((h) => (
                        <option key={h.value} value={h.value}>{h.text}</option>
                    ))}
                </select>
                {/* chevron */}
                <svg
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-70"
                    viewBox="0 0 20 20" fill="currentColor"
                >
                    <path d="M10 12l-4-4h8l-4 4z" />
                </svg>
            </div>

            {/* Minute */}
            <div className="w-1/2 relative">
                <select
                    value={minute}
                    onChange={(e) => updateTime(undefined, e.target.value)}
                    className="block w-full rounded-lg bg-black/30 text-white border border-white/10 px-3 py-3
                   focus:outline-none focus:ring-2 focus:ring-yellow-400/70 appearance-none"
                    required
                >
                    <option value="">Minute</option>
                    {minutes.map((m) => (
                        <option key={m} value={m}>{m}</option>
                    ))}
                </select>
                {/* chevron */}
                <svg
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-70"
                    viewBox="0 0 20 20" fill="currentColor"
                >
                    <path d="M10 12l-4-4h8l-4 4z" />
                </svg>
            </div>
        </div>
    );


};

const CalendarOptionsField = ({ value, onChange }) => {
    return (
        <div className="p-4 rounded-lg shadow-md border border-white/10 bg-white/10 backdrop-blur-md">
            <label className="block text-sm font-medium text-white mb-2">Calendar Options</label>

            <div className="grid gap-2 text-white">
                <label className="flex items-start gap-2 cursor-pointer">
                    <input
                        type="radio"
                        name="calendarOption"
                        value={1}
                        checked={Number(value) === 1}
                        onChange={() => onChange(1)}
                        className="mt-1 h-4 w-4 accent-yellow-400"
                    />
                    <span className="flex-1 leading-snug">
                    1 calendar event per astrological event
                </span>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                    <input
                        type="radio"
                        name="calendarOption"
                        value={2}
                        checked={Number(value) === 2}
                        onChange={() => onChange(2)}
                        className="mt-1 h-4 w-4 accent-yellow-400"
                    />
                    <span className="flex-1 leading-snug">
                    1 calendar event marking the start of the astrological event and 1 calendar event marking the end.
                </span>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                    <input
                        type="radio"
                        name="calendarOption"
                        value={3}
                        checked={Number(value) === 3}
                        onChange={() => onChange(3)}
                        className="mt-1 h-4 w-4 accent-yellow-400"
                    />
                    <span className="flex-1 leading-snug">
                    1 calendar event summing up all events of the day.
                </span>
                </label>
            </div>

            <p className="text-xs text-white/80 mt-2">
                You can choose how the calendar invites appear on your calendar app.
            </p>
        </div>
    );

};

const UserForm = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        dob: '',
        tob: '', // Time of Birth will be manually entered in 12-hour format
        birthplace: null,
        birthplaceName: '',
        advancedOptions: {
            moonTransits: true,
            moonAspects: true,
            moonAspectHouseRulers: false,
            aspectHouseRulers: true,
            outerPlanets: true,
            calendarOption: 1
        },
        orderId: ''
    });

    const [advancedUnderstanding, setAdvancedUnderstanding] = useState(false);
    const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const navigate = useNavigate();

    const defaultAdvancedOptions = {
        moonTransits: true,
        moonAspects: true,
        moonAspectHouseRulers: false,
        aspectHouseRulers: true,
        outerPlanets: true,
        calendarOption: 1
    };

    const [searchParams] = useSearchParams();
    const order_id = searchParams.get("order_id");
    let variant_id = searchParams.get("variant_id");


    const marketingParams = {
        // We check for both standard UTM tags and your custom shorthand keys
        source: searchParams.get("source") || "",
        campaign: searchParams.get("campaign") || "",
        ad_id: searchParams.get("ad_id") || "",
        adset_id: searchParams.get("adset_id") || "",
        gclid: searchParams.get("gclid") || "",   // Google Click ID
        fbclid: searchParams.get("fbclid") || ""  // Facebook Click ID
    };
    const handleCalendarOptionChange = (num) => {
        setFormData((prev) => ({
            ...prev,
            advancedOptions: {
                ...prev.advancedOptions,
                calendarOption: Number(num),   // ensure it’s numeric
            }
        }));
    };
    //TODO: Add form Id as unique GUID
    useEffect(() => {
        if (order_id) {
            setFormData((prevData) => ({
                ...prevData,
                orderId: order_id
            }));
        }
    }, [order_id]);

    const handleInputChange = (e) => {
        setFormData({...formData, [e.target.name]: e.target.value});
    };

    const handleCheckboxChange = (e) => {
        setFormData({
            ...formData,
            advancedOptions: {
                ...formData.advancedOptions,
                [e.target.name]: e.target.checked,
            },
        });
    };

    const handleAdvancedUnderstandingChange = (e) => {
        const isChecked = e.target.checked;
        setAdvancedUnderstanding(isChecked);

        if (!isChecked) {
            setFormData({
                ...formData,
                advancedOptions: defaultAdvancedOptions,
            });
        }
    };
    const isLeapYear = (year) => {
        return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
    };

    const getMaxDaysInMonth = (month, year) => {
        if ([4, 6, 9, 11].includes(month)) {
            return 30;
        } else if (month === 2) {
            return isLeapYear(year) ? 29 : 28;
        }
        return 31;
    };

// Validation function to be called in handleSubmit
    const validateBirthDate = () => {
        const {dob} = formData;

        if (!dob) return true; // No date entered yet, so no validation needed

        const [year, month, day] = dob.split('-').map(part => parseInt(part, 10));

        // Check if date is valid
        const maxDays = getMaxDaysInMonth(month, year);
        if (day > maxDays) {
            return `Invalid date: ${getMonthName(month)} ${year} has only ${maxDays} days. Please correct the date.`;
        }

        return true; // Date is valid
    };
    const getMonthName = (monthNum) => {
        const monthsText = [
            {value: 1, text: "January"},
            {value: 2, text: "February"},
            {value: 3, text: "March"},
            {value: 4, text: "April"},
            {value: 5, text: "May"},
            {value: 6, text: "June"},
            {value: 7, text: "July"},
            {value: 8, text: "August"},
            {value: 9, text: "September"},
            {value: 10, text: "October"},
            {value: 11, text: "November"},
            {value: 12, text: "December"}
        ];
        return monthsText.find(m => m.value === monthNum)?.text || "";
    };
    const handleLocationSelect = (latlng, name) => {
        setFormData({...formData, birthplace: latlng, birthplaceName: name});
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.birthplace) {
            alert("Please select a location by choosing from the suggestions or choosing directly from the map!");
            return;
        }
        const dateValidation = validateBirthDate();
        if (dateValidation !== true) {
            alert(dateValidation);
            return;
        }
        setIsConfirming(true);
    };

    const handleBack = () => {
        setIsConfirming(false);
    };

    const handleFinalSubmit = async () => {
        setFormSubmitted(true);
        console.log(formData);
        let selling_plan_id;

        // Shopify checkout parameters
        //YEARLY
        if (variant_id === '43591960264877') {
            console.log("Yearly");
            selling_plan_id = '1746796717'
        } else if (variant_id === '44540083404973') {
            selling_plan_id = '2214101165'
            console.log("14 Day Trial");
        } else {
            //if the variant id doesn't exist switch to monthly
            variant_id = '43591960232109'
            selling_plan_id = '1746731181'
            console.log("MONTHLY")
        }

        // Get form ID from URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const formId = urlParams.get('form_id');

        try {
            // Console log all the data that would be sent to backend
            console.log('=== FORM SUBMISSION DATA ===');
            console.log({
                custom_form_id: formId,
                variant_id: variant_id,
                selling_plan_id: selling_plan_id,
                form_data: formData,
                submitted_at: new Date().toISOString()
            });
            console.log('===========================');
            const backend_endpoint = "https://af03-users-gp-prod-jp.azurewebsites.net/api/new_form_submission"
            await fetch(backend_endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    custom_form_id: formId,
                    variant_id: variant_id,
                    selling_plan_id: selling_plan_id,
                    form_data: formData,
                    submitted_at: new Date().toISOString()
                })
            });
            // Add to cart with form ID and go directly to checkout
            let redirectUri = `https://galacticplanner.com/cart/add?` +
                `id=${variant_id}` +
                `&quantity=1` +
                `&selling_plan=${selling_plan_id}` +
                `&attributes[custom_form_id]=${formId}` +
                `&return_to=/checkout`;
            if (marketingParams.source) {
                redirectUri += `&attributes[source]=${encodeURIComponent(marketingParams.source)}`;
            }
            if (marketingParams.campaign) {
                redirectUri += `&attributes[campaign]=${encodeURIComponent(marketingParams.campaign)}`;
            }
            if (marketingParams.ad_id) {
                redirectUri += `&attributes[ad_id]=${encodeURIComponent(marketingParams.ad_id)}`;
            }
            if (marketingParams.adset_id) {
                redirectUri += `&attributes[adset_id]=${encodeURIComponent(marketingParams.adset_id)}`;
            }
            console.log(redirectUri)
            window.location.href = redirectUri;
        } catch (error) {
            console.error('Error submitting form:', error);
            navigate('/error');
        }
    };

    const formatTime = (tob) => {
        const [hours, minutes] = tob.split(':');
        const date = new Date();
        date.setHours(parseInt(hours), parseInt(minutes));
        return date.toLocaleString('en-US', {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
        });
    };

    const formatDate = (dob) => {
        const parts = dob.split('-');
        const utcDate = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
        return utcDate.toLocaleDateString('en-US', {
            timeZone: "UTC",
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-[#404f6b] via-[#2f3a52] to-black text-white">
            <div className="relative max-w-md mx-auto p-4 space-y-4 z-10" style={{fontFamily: 'Gabarito, sans-serif'}}>
                {/* Background Animation */}
                <PlanetAnimation/>

                <div className="flex justify-center mb-4 relative z-10">
                    <img src="/GPGoldLogo.png" alt="Galactic Planner Logo" className="max-w-[360px] w-full"/>
                </div>

                {!formSubmitted && !isConfirming ? (
                    <form onSubmit={handleSubmit} className="flex flex-col space-y-4 relative z-10">
                        <div className="space-y-2">
                            <div className="flex justify-center space-x-4 mb-2">
                                <FaStar className="text-xl animate-pulse" style={{color: '#C7932C'}}/>
                                <FaMoon className="text-xl" style={{color: '#B6E2DB'}}/>
                                <FaSun className="text-xl animate-spin-slow"
                                       style={{color: '#C7932C', animationDuration: '10s'}}/>
                            </div>
                            <h3 className="text-sm text-justify italic py-2 mt-1 text-white">
                                Thank you for purchasing the Galactic Planner! As a final step, please complete your
                                birth information below, so we can create and send you your personalized calendar. Once
                                you click submit, you will receive your Galactic Planner via email within 10 minutes.
                            </h3>
                        </div>

                        {/* Name Field */}
                        <FormField
                            label="Name"
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Your Name"
                            required={true}
                            icon={<FaStar className="text-sm" style={{color: '#C7932C'}}/>}
                        />

                        {/* Email Field */}
                        <FormField
                            label="Email"
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="Your Email"
                            required={true}
                            icon={<FaSun className="text-sm" style={{color: '#C7932C'}}/>}
                        />

                        {/* DOB */}
                        <div className="p-4 rounded-lg shadow-md border border-white/10 bg-white/10 backdrop-blur-md">
                            <div className="flex items-center mb-2">
                                <FaStar className="mr-2 animate-pulse text-[#C7932C]"/>
                                <label className="text-white text-sm font-medium">Date of Birth</label>
                            </div>
                            <DatePickerDropdown
                                value={formData.dob}
                                onChange={handleInputChange}
                                containerClassName="grid grid-cols-3 gap-3"
                                controlClassName="p-3 rounded-lg bg-black/30 text-white border border-white/10 focus:outline-none focus:ring-2 focus:ring-yellow-400/70"
                            />
                            <p className="text-xs text-white/80 mt-2">
                                Please enter your full birth date. Year must be 1900 or later.
                            </p>
                        </div>


                        {/* TOB */}
                        <div className="p-4 rounded-lg shadow-md border border-white/10 bg-white/10 backdrop-blur-md">
                            <div className="flex items-center mb-2">
                                <FaMoon className="mr-2 text-[#B6E2DB]"/>
                                <label className="text-white text-sm font-medium">Time of Birth</label>
                            </div>
                            <TimePickerDropdown
                                value={formData.tob}
                                onChange={handleInputChange}
                                containerClassName="grid grid-cols-2 gap-3"
                                controlClassName="p-3 rounded-lg bg-black/30 text-white border border-white/10 focus:outline-none focus:ring-2 focus:ring-yellow-400/70"
                            />
                            <p className="text-xs text-white/80 mt-2">
                                For accurate calculations please make sure the time and date of birth are accurate
                                within a 15-minute margin. We account for DST and timezone changes when relevant.
                            </p>
                        </div>


                        <BirthplaceField
                            onLocationSelect={handleLocationSelect}
                            initialLocation={formData.birthplace}
                            initialLocationName={formData.birthplaceName}
                        />
                        <CalendarOptionsField
                            value={formData.advancedOptions.calendarOption}
                            onChange={handleCalendarOptionChange}
                        />
                        <AdvancedOptionsField
                            showAdvancedOptions={showAdvancedOptions}
                            setShowAdvancedOptions={setShowAdvancedOptions}
                            advancedUnderstanding={advancedUnderstanding}
                            handleAdvancedUnderstandingChange={handleAdvancedUnderstandingChange}
                            formData={formData}
                            handleCheckboxChange={handleCheckboxChange}
                        />

                        {/* Terms */}
                        <div
                            className="p-4 rounded-lg shadow-[0_0_15px_rgba(199,147,44,0.4)] border backdrop-blur-md"
                            style={{
                                borderColor: '#C7932C',
                                backgroundColor: 'rgba(64, 79, 107, 0.45)' // 404f6b tone, translucent
                            }}
                        >
                            <div className="flex items-start">
                                <input
                                    type="checkbox"
                                    id="termsOfService"
                                    name="termsOfService"
                                    required
                                    className="mt-1 mr-3 h-5 w-5 rounded border border-white/20 focus:ring-2"
                                    style={{
                                        accentColor: '#C7932C'
                                    }}
                                />
                                <label htmlFor="termsOfService" className="text-sm text-white leading-relaxed">
                                    I agree to the{' '}
                                    <a
                                        href="https://galacticplanner.com/pages/website-terms-of-use"
                                        className="underline font-medium"
                                        style={{
                                            color: '#C7932C',
                                            textShadow: '0 0 4px rgba(199,147,44,0.9)'
                                        }}
                                    >
                                        Terms of Service
                                    </a>{' '}
                                    and{' '}
                                    <a
                                        href="https://galacticplanner.com/pages/privacy-policy"
                                        className="underline font-medium"
                                        style={{
                                            color: '#C7932C',
                                            textShadow: '0 0 4px rgba(199,147,44,0.9)'
                                        }}
                                    >
                                        Privacy Policy
                                    </a>.
                                </label>
                            </div>
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            className="inline-flex justify-center py-3 px-6 rounded-xl font-semibold text-black
             bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500
             transition-all shadow-md focus:outline-none focus:ring-2 focus:ring-yellow-400/70"
                        >
                            <FaStar className="mr-2 animate-pulse text-[#C7932C]"/>
                            Submit
                            <FaStar className="ml-2 animate-pulse text-[#C7932C]"/>
                        </button>


                        {/* Support */}
                        <div className="text-sm text-center text-white">
                            Having problems? Try visiting our <a href="https://galacticplanner.com/pages/main-faqs"
                                                                 className="underline" style={{
                            color: '#C7932C',
                            textShadow: '0 0 3px #C7932C'
                        }}>FAQs Page</a> or Contact us at <a href="mailto:hello@galacticplanner.com"
                                                             className="underline" style={{
                            color: '#C7932C',
                            textShadow: '0 0 3px #C7932C'
                        }}>hello@galacticplanner.com</a>
                        </div>
                    </form>
                ) : !formSubmitted && isConfirming ? (
                    <ConfirmationView
                        formData={formData}
                        formatDate={formatDate}
                        formatTime={formatTime}
                        handleFinalSubmit={handleFinalSubmit}
                        handleBack={handleBack}
                    />
                ) : (
                    <LoadingView/>
                )}

                {/* Nebula layer (behind canvas). You can delete this if the canvas background is enough. */}
                <div
                    className="fixed top-0 left-0 w-full h-full pointer-events-none overflow-hidden -z-20"
                    style={{background: 'radial-gradient(circle at center, #404f6b 0%, #2f3a52 45%, #000000 100%)'}}
                >
                    {Array.from({length: 40}).map((_, i) => (
                        <div
                            key={i}
                            className="absolute animate-pulse rounded-full"
                            style={{
                                top: `${Math.random() * 100}%`,
                                left: `${Math.random() * 100}%`,
                                width: `${Math.random() * 4 + 1}px`,
                                height: `${Math.random() * 4 + 1}px`,
                                backgroundColor: Math.random() > 0.7 ? '#C7932C' : '#FFFFFF',
                                animationDuration: `${Math.random() * 5 + 2}s`,
                                animationDelay: `${Math.random() * 5}s`,
                                opacity: Math.random() * 0.7 + 0.3,
                                boxShadow: '0 0 8px 1px rgba(255, 255, 255, 0.5)',
                            }}
                        />
                    ))}

                    {Array.from({length: 5}).map((_, i) => (
                        <div
                            key={`nebula-${i}`}
                            className="absolute opacity-10 rounded-full blur-3xl"
                            style={{
                                top: `${Math.random() * 100}%`,
                                left: `${Math.random() * 100}%`,
                                width: `${Math.random() * 250 + 100}px`,
                                height: `${Math.random() * 250 + 100}px`,
                                backgroundColor: i % 2 === 0 ? '#B6E2DB' : '#C7932C',
                                transform: `rotate(${Math.random() * 360}deg)`,
                            }}
                        />
                    ))}
                </div>
            </div>
        </div>
    );

};

export default UserForm;